import React, {useEffect, useLayoutEffect, useState} from 'react';
import uniqid from 'uniqid';
import {
	AdaptivityProvider,
	AppRoot,
	View,
	Panel,
	PanelHeader,
	Group,
	FormItem,
	Input,
	Button
} from '@vkontakte/vkui';
import '@vkontakte/vkui/dist/vkui.css';

import Home from './panels/Home';
import Admin from "./rest/Admin";
import AdminPanel from "./panels/AdminPanel";
import User from "./rest/User";
import { isCroppingPathname } from "./isCroppingPath";
import { isStatPathname } from "./isStatPath";
import Stat from "./rest/Stat";
import StatPanel, { StatGatePanel } from "./panels/StatPanel";

/** id заказа на основном сайте (синхрон с hash в URL) */
const ORDER_UNIQ_ID_MAIN = "printorder_order_uniq_id_v1";
/** отдельный id для /cropping — без hash, иначе после F5 подставлялся чужой #fragment и черновик не находился */
const ORDER_UNIQ_ID_CROPPING = "printorder_order_uniq_id_cropping_v1";

const orderUniqStorageKey = () => (isCroppingPathname() ? ORDER_UNIQ_ID_CROPPING : ORDER_UNIQ_ID_MAIN);

const stripOrderHashIfStatPath = () => {
	if (!isStatPathname()) {
		return;
	}
	try {
		if (window.location.hash) {
			const cleanUrl = window.location.pathname + window.location.search;
			history.replaceState(null, "", cleanUrl);
		}
	} catch (e) {
		/* ignore */
	}
};

const readOrderIdFromHash = () => {
	if (isStatPathname()) {
		return null;
	}
	try {
		const raw = String(window.location.hash || "").replace(/^#/, "").trim();
		if (!raw || raw.toLowerCase() === "admin") {
			return null;
		}
		return raw;
	} catch (e) {
		return null;
	}
};

const readOrderIdFromSessionStorageForKey = (storageKey) => {
	try {
		const s = String(sessionStorage.getItem(storageKey) || "").trim();
		if (!s || s.toLowerCase() === "admin") {
			return null;
		}
		return s;
	} catch (e) {
		return null;
	}
};

const readOrderIdFromLocalStorageForKey = (storageKey) => {
	try {
		const s = String(localStorage.getItem(storageKey) || "").trim();
		if (!s || s.toLowerCase() === "admin") {
			return null;
		}
		return s;
	} catch (e) {
		return null;
	}
};

const readOrderIdFromSessionStorage = () => readOrderIdFromSessionStorageForKey(orderUniqStorageKey());

const readOrderIdFromLocalStorage = () => readOrderIdFromLocalStorageForKey(orderUniqStorageKey());

const persistOrderUniqId = (id) => {
	if (isStatPathname()) {
		return;
	}
	const key = orderUniqStorageKey();
	try {
		sessionStorage.setItem(key, id);
	} catch (e) {
		/* private mode / quota */
	}
	try {
		localStorage.setItem(key, id);
	} catch (e) {
		/* private mode / quota */
	}
};

const newOrderUniqId = () => uniqid(String(Date.now()) + "_");

const getInitialOrderUniqId = () => {
	/* /stat — отдельный раздел, id заказа не используется */
	if (isStatPathname()) {
		return "stat_unused";
	}
	/* На /cropping не применять ветку #admin — иначе каждый F5 новый id без записи в crop-хранилище. */
	if (!isCroppingPathname() && window.location.hash === "#admin") {
		return newOrderUniqId();
	}
	/* /cropping: не берём order id из hash (фрагмент часто остаётся от основного сайта или пустой) */
	if (isCroppingPathname()) {
		let fromSession = readOrderIdFromSessionStorageForKey(ORDER_UNIQ_ID_CROPPING);
		let fromLocal = readOrderIdFromLocalStorageForKey(ORDER_UNIQ_ID_CROPPING);
		if (!fromSession && !fromLocal) {
			const legacyS = readOrderIdFromSessionStorageForKey(ORDER_UNIQ_ID_MAIN);
			const legacyL = readOrderIdFromLocalStorageForKey(ORDER_UNIQ_ID_MAIN);
			const legacy = legacyS || legacyL;
			if (legacy) {
				persistOrderUniqId(legacy);
				return legacy;
			}
		}
		if (fromSession) {
			persistOrderUniqId(fromSession);
			return fromSession;
		}
		if (fromLocal) {
			persistOrderUniqId(fromLocal);
			return fromLocal;
		}
		const next = newOrderUniqId();
		persistOrderUniqId(next);
		return next;
	}
	const fromHash = readOrderIdFromHash();
	if (fromHash) {
		persistOrderUniqId(fromHash);
		return fromHash;
	}
	const fromSession = readOrderIdFromSessionStorage();
	if (fromSession) {
		persistOrderUniqId(fromSession);
		return fromSession;
	}
	const fromLocal = readOrderIdFromLocalStorage();
	if (fromLocal) {
		persistOrderUniqId(fromLocal);
		return fromLocal;
	}
	return newOrderUniqId();
};

const initialActivePanel = () => {
	try {
		if (isStatPathname()) {
			return Stat.getToken() ? "stat" : "stat_gate";
		}
		if (isCroppingPathname() && !User.getCropToken()) {
			return "crop_gate";
		}
		if (window.location.hash === "#admin") {
			return Admin.getToken() ? "admin" : "admin_gate";
		}
	} catch (e) {
		/* ignore */
	}
	return "home";
};

const CropGatePanel = ({id, onAuthenticated}) => {
	const [login, setLogin] = useState("crop");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);

	const submit = () => {
		setError("");
		const u = login.trim();
		const p = password;
		if (!u || !p) {
			setError("Введите логин и пароль");
			return;
		}
		setBusy(true);
		User.cropAuth(u, p).then((r) => {
			setBusy(false);
			if (r.data.auth && r.data.token) {
				User.setCropToken(r.data.token);
				onAuthenticated();
			} else {
				setError("Неверный логин или пароль");
			}
		}).catch(() => {
			setBusy(false);
			setError("Ошибка сети или сервера");
		});
	};

	return (
		<View activePanel={id}>
			<Panel id={id}>
				<PanelHeader>Вход в режим обрезки</PanelHeader>
				<Group>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							submit();
						}}
					>
						<FormItem top="Логин">
							<Input
								type="text"
								value={login}
								onChange={(e) => setLogin(e.currentTarget.value)}
								autoComplete="username"
								disabled={busy}
							/>
						</FormItem>
						<FormItem top="Пароль">
							<Input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.currentTarget.value)}
								autoComplete="current-password"
								disabled={busy}
							/>
						</FormItem>
						{error ? (
							<FormItem>
								<div style={{color: "var(--vkui--color_text_negative)", fontSize: 14}}>{error}</div>
							</FormItem>
						) : null}
						<FormItem>
							<Button type="submit" size="l" stretched loading={busy} disabled={busy}>
								Войти
							</Button>
						</FormItem>
					</form>
				</Group>
			</Panel>
		</View>
	);
};

const AdminGatePanel = ({id, onAuthenticated}) => {
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);

	const submit = () => {
		setError("");
		const p = password;
		if (!p) {
			setError("Введите пароль");
			return;
		}
		setBusy(true);
		Admin.auth(p).then((r) => {
			setBusy(false);
			if (r.data.auth && r.data.token) {
				Admin.setToken(r.data.token);
				onAuthenticated();
			} else {
				setError("Неверный пароль");
			}
		}).catch(() => {
			setBusy(false);
			setError("Ошибка сети или сервера");
		});
	};

	return (
		<View activePanel={id}>
			<Panel id={id}>
				<PanelHeader>Админка</PanelHeader>
				<Group>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							submit();
						}}
					>
						<FormItem top="Пароль администратора">
							<Input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.currentTarget.value)}
								autoComplete="current-password"
								disabled={busy}
							/>
						</FormItem>
						{error ? (
							<FormItem>
								<div style={{color: "var(--vkui--color_text_negative)", fontSize: 14}}>{error}</div>
							</FormItem>
						) : null}
						<FormItem>
							<Button type="submit" size="l" stretched loading={busy} disabled={busy}>
								Войти
							</Button>
						</FormItem>
					</form>
				</Group>
			</Panel>
		</View>
	);
};

const App = () => {
	const [activePanel, setActivePanel] = useState(initialActivePanel);
	const [fetchedUser, setUser] = useState(null);
	const [popout, setPopout] = useState(null);
	const [orderUniqId, setOrderUniqId] = useState(() => getInitialOrderUniqId());

	useLayoutEffect(() => {
		stripOrderHashIfStatPath();
	}, []);

	const resetOrderSession = () => {
		if (isStatPathname()) {
			return;
		}
		const next = newOrderUniqId();
		setOrderUniqId(next);
		try {
			window.location.hash = next;
			persistOrderUniqId(next);
		} catch (e) {
			/* ignore */
		}
	};

	useEffect(() => {
		const onAdminExpired = () => {
			Admin.setToken(null);
			if (window.location.hash === "#admin") {
				setActivePanel("admin_gate");
			}
		};
		const onCropExpired = () => {
			User.setCropToken("");
			if (isCroppingPathname()) {
				setActivePanel("crop_gate");
			}
		};
		const onStatExpired = () => {
			Stat.setToken(null);
			if (isStatPathname()) {
				setActivePanel("stat_gate");
			}
		};
		window.addEventListener("printorder:admin-session-expired", onAdminExpired);
		window.addEventListener("printorder:crop-session-expired", onCropExpired);
		window.addEventListener("printorder:stat-session-expired", onStatExpired);
		return () => {
			window.removeEventListener("printorder:admin-session-expired", onAdminExpired);
			window.removeEventListener("printorder:crop-session-expired", onCropExpired);
			window.removeEventListener("printorder:stat-session-expired", onStatExpired);
		};
	}, []);

	useEffect(() => {
		if (isStatPathname()) {
			document.title = "Статистика";
			if (Stat.getToken()) {
				setActivePanel("stat");
			}
			return;
		}
		if (isCroppingPathname()) {
			document.title = "РЕЖИМ ОБРЕЗКИ";
			try {
				sessionStorage.removeItem("cropAuth");
			} catch (e) {
				/* ignore */
			}
			if (User.getCropToken()) {
				setActivePanel("home");
			}
			return;
		}
		if (window.location.hash === "#admin") {
			document.title = "PRINTORDER";
			if (Admin.getToken()) {
				setActivePanel("admin");
			}
			return;
		}
		document.title = "PRINTORDER";
	}, []);

	useEffect(() => {
		if (isStatPathname()) {
			stripOrderHashIfStatPath();
			return;
		}
		if (isCroppingPathname() || window.location.hash === "#admin") {
			return;
		}
		if (!readOrderIdFromHash()) {
			try {
				window.location.hash = orderUniqId;
			} catch (e) {
				/* ignore */
			}
		}
	}, [orderUniqId]);

	/* Иначе React держит старый uniqId, а в hash уже другой заказ — persist пишет «не тот» orderId,
	   после F5 readPersistedOrderDraft не находит черновик; новая вкладка стартует с hash и совпадает. */
	useEffect(() => {
		const syncOrderIdFromLocation = () => {
			if (isStatPathname() || isCroppingPathname() || window.location.hash === "#admin") {
				return;
			}
			const fromHash = readOrderIdFromHash();
			if (!fromHash) {
				return;
			}
			setOrderUniqId((prev) => {
				if (prev === fromHash) {
					return prev;
				}
				persistOrderUniqId(fromHash);
				return fromHash;
			});
		};
		window.addEventListener("hashchange", syncOrderIdFromLocation);
		window.addEventListener("pageshow", syncOrderIdFromLocation);
		return () => {
			window.removeEventListener("hashchange", syncOrderIdFromLocation);
			window.removeEventListener("pageshow", syncOrderIdFromLocation);
		};
	}, []);

	const go = e => {
		setActivePanel(e.currentTarget.dataset.to);
	};

	return (
		<AdaptivityProvider>
			<AppRoot>
				<View activePanel={activePanel} popout={popout}>
					<StatGatePanel id="stat_gate" onAuthenticated={() => setActivePanel("stat")} />
					<StatPanel id="stat" onLogout={() => setActivePanel("stat_gate")} />
					<CropGatePanel id="crop_gate" onAuthenticated={() => setActivePanel("home")} />
					<AdminGatePanel id="admin_gate" onAuthenticated={() => setActivePanel("admin")} />
					<Home
						id="home"
						fetchedUser={fetchedUser}
						go={go}
						uniqId={orderUniqId}
						onOrderSessionReset={resetOrderSession}
					/>
					<AdminPanel id='admin' fetchedUser={fetchedUser} go={go} />
				</View>
			</AppRoot>
		</AdaptivityProvider>
	);
}

export default App;
